import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useDesignedKeyboard } from '@/hooks/keyboard/use-designed-keyboard';
import { useIsDesignCompleted } from '@/hooks/keyboard/use-design-completion';
import { useFrontWeightDesign, useStrikeWeightDesign, useStrikeWeightRatioDesign, useWippenSupportSpringsDesign } from '@/hooks/store/use-design-store';
import { useTranslation } from '@/hooks/use-translation';
import { SmoothStrategy } from '@/lib/geometry/curve-smoother/smooth-strategy.enum';
import { formatGrams, formatRatio } from '@/lib/format/format-weight';
import { feltBalanceWeight } from '@/lib/piano/touch-design/felt-balance-weight';
import { FrontWeightDesignMode } from '@/lib/piano/touch-design/design/front-weight-design';
import { StrikeWeightDesignMode } from '@/lib/piano/touch-design/design/strike-weight-design';
import { StrikeWeightRatioDesignMode } from '@/lib/piano/touch-design/design/strike-weight-ratio-design';
import { WippenSupportSpringsDesignMode } from '@/lib/piano/touch-design/design/wippen-support-springs-design';

/**
 * Between two parts of a step's summary. The space before the dot is one a
 * line never breaks at: a narrow tile wraps after the dot, never before it,
 * so no line starts with one.
 */
const SEPARATOR = '\u00a0· ';
export type DesignStepId = 'springs' | 'front-weight' | 'strike-weight' | 'ratio' | 'preview' | 'sheet';
export interface DesignStep {
  id: DesignStepId;
  /** Result tiles stay closed until the design resolves. */
  isAvailable: boolean;
  kind: 'parameter' | 'result';
  /** The tile's caption, numbered: "1 · Front Weight". */
  label: string;
  /** The same step without its number, for a printed recap. */
  name: string;
  to: string;
  /** Where the parameter stands right now: "Computed · BW 39 g". */
  value: string;
}
const average = (values: (number | null)[]): number | null => {
  const measured = values.filter((value): value is number => value !== null);
  return measured.length === 0 ? null : measured.reduce((total, value) => total + value, 0) / measured.length;
};

/**
 * The six tiles of the design banner.
 *
 * They turn four separate pages into one form: each tile states where its
 * parameter stands, so the reader can see the whole design from any of them.
 */
export const useDesignSteps = () => {
  const $ = _c(104);
  const {
    currentLanguage,
    t
  } = useTranslation();
  const designedKeyboard = useDesignedKeyboard();
  const isDesignCompleted = useIsDesignCompleted(designedKeyboard);
  const springs = useWippenSupportSpringsDesign();
  const frontWeight = useFrontWeightDesign();
  const strikeWeight = useStrikeWeightDesign();
  const ratio = useStrikeWeightRatioDesign();
  let t0;
  if ($[0] !== currentLanguage || $[1] !== springs.wippenSupportSpringsDesignMode || $[2] !== springs.wippenSupportSpringsDesignTarget || $[3] !== t) {
    t0 = () => {
      const target = springs.wippenSupportSpringsDesignTarget;
      const tension = target !== null && typeof target === "object" ? `${target.numberOfSprings}${SEPARATOR}${formatGrams(target.springBalanceWeight, currentLanguage)}` : null;
      switch (springs.wippenSupportSpringsDesignMode) {
        case WippenSupportSpringsDesignMode.AsMeasured:
          {
            return t("As measured");
          }
        case WippenSupportSpringsDesignMode.Constant:
          {
            return [t("Constant"), tension].filter(Boolean).join(SEPARATOR);
          }
        case WippenSupportSpringsDesignMode.SmoothTransition:
          {
            return [t("Tapered"), tension].filter(Boolean).join(SEPARATOR);
          }
        default:
          {
            return t("None");
          }
      }
    };
    $[0] = currentLanguage;
    $[1] = springs.wippenSupportSpringsDesignMode;
    $[2] = springs.wippenSupportSpringsDesignTarget;
    $[3] = t;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const springsValue = t0;
  let t1;
  if ($[5] !== currentLanguage || $[6] !== frontWeight.frontWeightDesignComputedBalanceWeightTarget || $[7] !== frontWeight.frontWeightDesignMode || $[8] !== frontWeight.frontWeightDesignStandardTarget || $[9] !== t) {
    t1 = () => {
      switch (frontWeight.frontWeightDesignMode) {
        case FrontWeightDesignMode.Computed:
          {
            return `${t("Computed")}${SEPARATOR}${t("BW {{weight}}", {
              weight: formatGrams(frontWeight.frontWeightDesignComputedBalanceWeightTarget, currentLanguage)
            })}`;
          }
        case FrontWeightDesignMode.StandardCurves:
          {
            return t("Curve FW#{{level}}", {
              level: frontWeight.frontWeightDesignStandardTarget ?? ""
            });
          }
        default:
          {
            return t("As measured");
          }
      }
    };
    $[5] = currentLanguage;
    $[6] = frontWeight.frontWeightDesignComputedBalanceWeightTarget;
    $[7] = frontWeight.frontWeightDesignMode;
    $[8] = frontWeight.frontWeightDesignStandardTarget;
    $[9] = t;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const frontWeightValue = t1;
  let t2;
  if ($[11] !== currentLanguage || $[12] !== strikeWeight.strikeWeightDesignComputedBalanceWeightTarget || $[13] !== strikeWeight.strikeWeightDesignMode || $[14] !== strikeWeight.strikeWeightDesignStandarTarget || $[15] !== t) {
    t2 = () => {
      switch (strikeWeight.strikeWeightDesignMode) {
        case StrikeWeightDesignMode.Computed:
          {
            return `${t("Computed")}${SEPARATOR}${t("BW {{weight}}", {
              weight: formatGrams(strikeWeight.strikeWeightDesignComputedBalanceWeightTarget, currentLanguage)
            })}`;
          }
        case StrikeWeightDesignMode.StandardCurves:
          {
            return t("Curve {{level}}", {
              level: String(strikeWeight.strikeWeightDesignStandarTarget ?? "")
            });
          }
        case StrikeWeightDesignMode.Smoothed:
          {
            return t("Smoothed curve");
          }
        default:
          {
            return t("As measured");
          }
      }
    };
    $[11] = currentLanguage;
    $[12] = strikeWeight.strikeWeightDesignComputedBalanceWeightTarget;
    $[13] = strikeWeight.strikeWeightDesignMode;
    $[14] = strikeWeight.strikeWeightDesignStandarTarget;
    $[15] = t;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  const strikeWeightValue = t2;
  let t3;
  if ($[17] !== currentLanguage || $[18] !== ratio.strikeWeightRatioDesignLatestFixedTarget || $[19] !== ratio.strikeWeightRatioDesignLatestSmoothTarget || $[20] !== ratio.strikeWeightRatioDesignMode || $[21] !== t) {
    t3 = () => {
      switch (ratio.strikeWeightRatioDesignMode) {
        case StrikeWeightRatioDesignMode.FixedValue:
          {
            return `${t("Constant")}${SEPARATOR}${formatRatio(typeof ratio.strikeWeightRatioDesignLatestFixedTarget === "number" ? ratio.strikeWeightRatioDesignLatestFixedTarget : null, currentLanguage)}`;
          }
        case StrikeWeightRatioDesignMode.Smoothed:
          {
            return ratio.strikeWeightRatioDesignLatestSmoothTarget === SmoothStrategy.Mean ? t("Mean") : t("Median");
          }
        default:
          {
            return t("As measured");
          }
      }
    };
    $[17] = currentLanguage;
    $[18] = ratio.strikeWeightRatioDesignLatestFixedTarget;
    $[19] = ratio.strikeWeightRatioDesignLatestSmoothTarget;
    $[20] = ratio.strikeWeightRatioDesignMode;
    $[21] = t;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  const ratioValue = t3;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[23] !== currentLanguage || $[24] !== designedKeyboard || $[25] !== frontWeightValue || $[26] !== isDesignCompleted || $[27] !== ratioValue || $[28] !== springsValue || $[29] !== strikeWeightValue || $[30] !== t) {
    const designedKeys = designedKeyboard.getKeys().filter(_temp);
    const designedCount = designedKeys.length;
    const designedBalanceWeights = designedKeys.map(_temp2);
    let t16;
    if ($[43] !== t) {
      t16 = t("Wippen Support Springs", {
        ns: "navigation"
      });
      $[43] = t;
      $[44] = t16;
    } else {
      t16 = $[44];
    }
    const t17 = `1 · ${t16}`;
    let t18;
    if ($[45] !== t) {
      t18 = t("Wippen Support Springs", {
        ns: "navigation"
      });
      $[45] = t;
      $[46] = t18;
    } else {
      t18 = $[46];
    }
    let t19;
    if ($[47] !== springsValue) {
      t19 = springsValue();
      $[47] = springsValue;
      $[48] = t19;
    } else {
      t19 = $[48];
    }
    if ($[49] !== t17 || $[50] !== t18 || $[51] !== t19) {
      t11 = {
        id: "springs" as const,
        isAvailable: true,
        kind: "parameter" as const,
        label: t17,
        name: t18,
        to: "/design/wippen-support-springs",
        value: t19
      };
      $[49] = t17;
      $[50] = t18;
      $[51] = t19;
      $[52] = t11;
    } else {
      t11 = $[52];
    }
    let t20;
    if ($[53] !== t) {
      t20 = t("Front Weight", {
        ns: "navigation"
      });
      $[53] = t;
      $[54] = t20;
    } else {
      t20 = $[54];
    }
    const t21 = `2 · ${t20}`;
    let t22;
    if ($[55] !== t) {
      t22 = t("Front Weight", {
        ns: "navigation"
      });
      $[55] = t;
      $[56] = t22;
    } else {
      t22 = $[56];
    }
    let t23;
    if ($[57] !== frontWeightValue) {
      t23 = frontWeightValue();
      $[57] = frontWeightValue;
      $[58] = t23;
    } else {
      t23 = $[58];
    }
    if ($[59] !== t21 || $[60] !== t22 || $[61] !== t23) {
      t12 = {
        id: "front-weight" as const,
        isAvailable: true,
        kind: "parameter" as const,
        label: t21,
        name: t22,
        to: "/design/front-weight",
        value: t23
      };
      $[59] = t21;
      $[60] = t22;
      $[61] = t23;
      $[62] = t12;
    } else {
      t12 = $[62];
    }
    let t24;
    if ($[63] !== t) {
      t24 = t("Strike Weight", {
        ns: "navigation"
      });
      $[63] = t;
      $[64] = t24;
    } else {
      t24 = $[64];
    }
    const t25 = `3 · ${t24}`;
    let t26;
    if ($[65] !== t) {
      t26 = t("Strike Weight", {
        ns: "navigation"
      });
      $[65] = t;
      $[66] = t26;
    } else {
      t26 = $[66];
    }
    let t27;
    if ($[67] !== strikeWeightValue) {
      t27 = strikeWeightValue();
      $[67] = strikeWeightValue;
      $[68] = t27;
    } else {
      t27 = $[68];
    }
    if ($[69] !== t25 || $[70] !== t26 || $[71] !== t27) {
      t13 = {
        id: "strike-weight" as const,
        isAvailable: true,
        kind: "parameter" as const,
        label: t25,
        name: t26,
        to: "/design/strike-weight",
        value: t27
      };
      $[69] = t25;
      $[70] = t26;
      $[71] = t27;
      $[72] = t13;
    } else {
      t13 = $[72];
    }
    let t28;
    if ($[73] !== t) {
      t28 = t("Strike Weight Ratio", {
        ns: "navigation"
      });
      $[73] = t;
      $[74] = t28;
    } else {
      t28 = $[74];
    }
    const t29 = `4 · ${t28}`;
    let t30;
    if ($[75] !== t) {
      t30 = t("Strike Weight Ratio", {
        ns: "navigation"
      });
      $[75] = t;
      $[76] = t30;
    } else {
      t30 = $[76];
    }
    let t31;
    if ($[77] !== ratioValue) {
      t31 = ratioValue();
      $[77] = ratioValue;
      $[78] = t31;
    } else {
      t31 = $[78];
    }
    if ($[79] !== t29 || $[80] !== t30 || $[81] !== t31) {
      t14 = {
        id: "ratio" as const,
        isAvailable: true,
        kind: "parameter" as const,
        label: t29,
        name: t30,
        to: "/design/strike-weight-ratio",
        value: t31
      };
      $[79] = t29;
      $[80] = t30;
      $[81] = t31;
      $[82] = t14;
    } else {
      t14 = $[82];
    }
    let t32;
    if ($[83] !== t) {
      t32 = t("Touch Weight Preview", {
        ns: "navigation"
      });
      $[83] = t;
      $[84] = t32;
    } else {
      t32 = $[84];
    }
    t15 = {
      id: "preview" as const,
      isAvailable: isDesignCompleted,
      kind: "result" as const,
      label: `→ ${t32}`,
      name: t("Touch Weight Preview", {
        ns: "navigation"
      }),
      to: "/design/touch-weight-preview",
      value: t("BW {{weight}}", {
        weight: formatGrams(average(designedBalanceWeights), currentLanguage)
      })
    };
    t4 = "sheet" as const;
    t5 = isDesignCompleted;
    t6 = "result" as const;
    let t33;
    if ($[85] !== t) {
      t33 = t("Adjustment Sheet", {
        ns: "navigation"
      });
      $[85] = t;
      $[86] = t33;
    } else {
      t33 = $[86];
    }
    t7 = `→ ${t33}`;
    if ($[87] !== t) {
      t8 = t("Adjustment Sheet", {
        ns: "navigation"
      });
      $[87] = t;
      $[88] = t8;
    } else {
      t8 = $[88];
    }
    t9 = "/design/adjustment-sheet";
    t10 = t("{{count}} keys", {
      count: designedCount,
      ns: "navigation"
    });
    $[23] = currentLanguage;
    $[24] = designedKeyboard;
    $[25] = frontWeightValue;
    $[26] = isDesignCompleted;
    $[27] = ratioValue;
    $[28] = springsValue;
    $[29] = strikeWeightValue;
    $[30] = t;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
    $[37] = t4;
    $[38] = t5;
    $[39] = t6;
    $[40] = t7;
    $[41] = t8;
    $[42] = t9;
  } else {
    t10 = $[31];
    t11 = $[32];
    t12 = $[33];
    t13 = $[34];
    t14 = $[35];
    t15 = $[36];
    t4 = $[37];
    t5 = $[38];
    t6 = $[39];
    t7 = $[40];
    t8 = $[41];
    t9 = $[42];
  }
  let t16;
  if ($[89] !== t10 || $[90] !== t4 || $[91] !== t5 || $[92] !== t6 || $[93] !== t7 || $[94] !== t8 || $[95] !== t9) {
    t16 = {
      id: t4,
      isAvailable: t5,
      kind: t6,
      label: t7,
      name: t8,
      to: t9,
      value: t10
    };
    $[89] = t10;
    $[90] = t4;
    $[91] = t5;
    $[92] = t6;
    $[93] = t7;
    $[94] = t8;
    $[95] = t9;
    $[96] = t16;
  } else {
    t16 = $[96];
  }
  let t17;
  if ($[97] !== t11 || $[98] !== t12 || $[99] !== t13 || $[100] !== t14 || $[101] !== t15 || $[102] !== t16) {
    t17 = [t11, t12, t13, t14, t15, t16];
    $[97] = t11;
    $[98] = t12;
    $[99] = t13;
    $[100] = t14;
    $[101] = t15;
    $[102] = t16;
    $[103] = t17;
  } else {
    t17 = $[103];
  }
  return t17;
};
function _temp(key) {
  return key.payload.downWeightWithoutSpringSupport !== null;
}
function _temp2(key_0) {
  return feltBalanceWeight(key_0.payload);
}